import { useCallback, useEffect, useMemo } from 'react';
import { throttle } from 'lodash';
import { useRealtimeEvent } from '../../../realtime/useRealtimeEvent';
import { useInvalidateOutreach } from './useInvalidateOutreach';

/** `outreach.find.progress` reads `campaigns.live` again at most one time in this time. */
export const OUTREACH_FIND_PROGRESS_THROTTLE_MS = 5000;
/** `outreach.message.updated` reads `outreach.home` again at most one time in this time. */
export const OUTREACH_MESSAGE_HOME_THROTTLE_MS = 10000;

/**
 * Keeps the open Outreach page fresh (plan Global Constraints: no poll while the socket is up).
 * Each event holds ids only; the page reads the data again through tRPC. An event of another artist
 * of the workspace reads the open page again too: each query has the artist id in its input.
 *
 * Two events come often: `outreach.find.progress` (up to one in 2 s while a find runs) and
 * `outreach.message.updated` (one for each sent pitch). Their full reads are throttled: the first
 * event reads at once, and more events in the time make one read at its end. A reply, a placement
 * and a campaign change read at once.
 */
export function useOutreachRealtime(): void {
  // `campaigns` holds `campaigns.live`, `campaigns.messages` (the People list), `campaigns.results`,
  // `campaigns.resultsAll` and `campaigns.poolSummary` too (design outreach-v2 g, n): a pitch, a
  // reply and an add read the campaign page and its results again.
  const invalidate = useInvalidateOutreach();

  const throttled = useMemo(
    () => ({
      live: throttle(() => invalidate(['live']), OUTREACH_FIND_PROGRESS_THROTTLE_MS, {
        leading: true,
        trailing: true,
      }),
      home: throttle(() => invalidate(['home']), OUTREACH_MESSAGE_HOME_THROTTLE_MS, {
        leading: true,
        trailing: true,
      }),
    }),
    [invalidate]
  );
  useEffect(
    () => () => {
      throttled.live.cancel();
      throttled.home.cancel();
    },
    [throttled]
  );

  const onProfile = useCallback(() => invalidate(['profile', 'home', 'targets', 'live']), [invalidate]);
  const onCampaign = useCallback(() => invalidate(['campaigns', 'review', 'home']), [invalidate]);
  const onMessage = useCallback(() => {
    invalidate(['review', 'campaigns']);
    throttled.home();
  }, [invalidate, throttled]);
  const onReply = useCallback(() => invalidate(['inbox', 'home', 'campaigns', 'contacts']), [invalidate]);
  const onPlacement = useCallback(() => invalidate(['campaigns', 'home', 'inbox']), [invalidate]);
  const onMailbox = useCallback(() => invalidate(['mailboxes', 'home', 'live']), [invalidate]);
  // A target find runs: only the search counters of the live screen.
  const onFindProgress = useCallback(() => throttled.live(), [throttled]);

  useRealtimeEvent('outreach.profile.updated', onProfile);
  useRealtimeEvent('outreach.campaign.updated', onCampaign);
  useRealtimeEvent('outreach.message.updated', onMessage);
  useRealtimeEvent('outreach.reply.received', onReply);
  useRealtimeEvent('outreach.placement.found', onPlacement);
  useRealtimeEvent('outreach.mailbox.updated', onMailbox);
  useRealtimeEvent('outreach.find.progress', onFindProgress);
}
